import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Check, Medal, Award, Crown, Gift } from "lucide-react";

const TIER_META = {
  bronze: { icon: Medal, ring: "border-amber-600/30", accent: "text-amber-700", btn: "" },
  silver: { icon: Award, ring: "border-slate-400/50", accent: "text-slate-500", btn: "", popular: true },
  gold: { icon: Crown, ring: "border-amber-400 ring-2 ring-amber-400/40", accent: "text-amber-500", btn: "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700" },
};

export default function Pricing() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState({});
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/meta").then((r) => setTiers(r.data.tiers));
    if (user) api.get("/subscription").then((r) => setCurrent(r.data.subscription?.tier || null)).catch(() => {});
  }, [user]);

  const subscribe = async (tierKey, lookupKey) => {
    if (!user) { navigate("/login"); return; }
    setBusy(tierKey);
    try {
      const res = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de paiement");
      setBusy("");
    }
  };

  const order = ["bronze", "silver", "gold"];

  const fmtLimit = (tier, val, unlimitedKey) =>
    typeof val === "number" && val >= 99 ? t(unlimitedKey) : val;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="text-center mb-14">
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-3">{t("pricing_title")}</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t("pricing_sub")}</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto items-start">
        <div data-testid="pricing-card-free"
          className="relative rounded-3xl border border-border bg-card p-8 shadow-sm">
          <div className="grid place-items-center h-12 w-12 rounded-xl bg-secondary mb-5 text-emerald-600">
            <Gift className="h-6 w-6" />
          </div>
          <h3 className="font-display text-2xl font-bold">{t("free_name")}</h3>
          <div className="mt-4 mb-6">
            <span className="font-display text-4xl font-extrabold">0$</span>
            <span className="text-muted-foreground">{t("per_month")}</span>
          </div>
          <ul className="space-y-3 mb-8 text-sm">
            <li className="flex items-center gap-2.5"><Check className="h-4 w-4 text-emerald-600 shrink-0" /><span>{t("free_f1")}</span></li>
            <li className="flex items-center gap-2.5"><Check className="h-4 w-4 text-emerald-600 shrink-0" /><span><strong>1</strong> {t("leads_wk")}</span></li>
            <li className="flex items-center gap-2.5 text-muted-foreground"><Check className="h-4 w-4 shrink-0 opacity-40" /><span>{t("free_f3")}</span></li>
          </ul>
          <Button variant="outline" className="w-full" disabled={user && current === "free"}
            data-testid="subscribe-free-btn"
            onClick={() => navigate(user ? "/feed" : "/register")}>
            {user && current === "free" ? t("current_plan") : t("start_free")}
          </Button>
        </div>
        {order.map((key) => {
          const tier = tiers[key];
          if (!tier) return null;
          const m = TIER_META[key];
          const Icon = m.icon;
          const isCurrent = current === key;
          return (
            <div key={key} data-testid={`pricing-card-${key}`}
              className={`relative rounded-3xl border bg-card p-8 shadow-sm ${m.ring}`}>
              {m.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary text-primary-foreground text-xs font-bold px-3 py-1">
                  {t("popular")}
                </div>
              )}
              <div className={`grid place-items-center h-12 w-12 rounded-xl bg-secondary mb-5 ${m.accent}`}>
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="font-display text-2xl font-bold">{tier.name}</h3>
              <div className="mt-4 mb-6">
                <span className="font-display text-4xl font-extrabold">{tier.price}$</span>
                <span className="text-muted-foreground">{t("per_month")}</span>
                <span className="block text-xs text-muted-foreground mt-1">{t("plus_tax")}</span>
              </div>
              <ul className="space-y-3 mb-8 text-sm">
                <li className="flex items-center gap-2.5">
                  <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                  <span><strong>{fmtLimit(tier, tier.leads_per_month, "unlimited")}</strong> {t("leads_mo")}</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                  <span>{key === "gold" ? t("all_sectors") : <><strong>{tier.sectors}</strong> {t("sectors_n")}</>}</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                  <span>{key === "gold" ? t("all_locations") : <><strong>{tier.locations}</strong> {t("locations_n")}</>}</span>
                </li>
              </ul>
              <p className="text-xs text-muted-foreground mb-3 text-center">
                {t("trial_note")} {tier.price}${t("per_month")}
              </p>
              <Button className={`w-full ${m.btn}`} disabled={isCurrent || busy === key}
                data-testid={`subscribe-${key}-btn`}
                onClick={() => subscribe(key, tier.key)}>
                {isCurrent ? t("current_plan") : busy === key ? t("processing") : t("subscribe_trial")}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
